import {MediaMatcher} from '@angular/cdk/layout';
import {AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {NavigationStart, Params, Router} from '@angular/router';
import {Observable, Subject} from 'rxjs';
import {filter, takeUntil} from 'rxjs/operators';
import {AventiObject} from 'src/app/models/AventiObject';
import {Checklist} from 'src/app/models/Checklist';
import {AventiFile} from 'src/app/models/File';
import {User} from 'src/app/models/User';
import {ChecklistService} from 'src/app/services/checklist.service';
import {DateService} from 'src/app/services/date.service';
import {ObjectService} from 'src/app/services/object.service';
import {SharedService} from 'src/app/services/shared.service';
import {environment} from 'src/environments/environment';
import {Table} from 'primeng/table';

import {FilterMetadata, SortMeta} from 'primeng/api';
import {SortButton} from 'src/app/models/PrimeNG';
import {TableService} from 'src/app/services/prime/table.service';
import {PopupService} from 'src/app/services/popup.service';
import {IdService} from 'src/app/services/id.service';
import {ConfirmComponent} from 'src/app/components/common/bottom-sheet/confirm/confirm.component';
import {MatBottomSheet} from '@angular/material/bottom-sheet';
import {SnackbarService} from 'src/app/services/snackbar.service';
import {ExcelService} from 'src/app/services/excel.service';
import {FileService} from 'src/app/services/file.service';

function getPdfDeliveredToCustomerBoolean(checklist: Checklist) {
  return (checklist.status === 'signed' && checklist.pdfexports?.length > 0) ?
    !!checklist.PDFDeliveredToCustomer?.status : null;
}

// Helper to ensure unique filename
function getUniqueFileName(baseName: string, fileNames: string[]): string {
  let name = baseName;
  let ext = '';
  const dotIdx = baseName.lastIndexOf('.');
  if (dotIdx !== -1) {
    name = baseName.substring(0, dotIdx);
    ext = baseName.substring(dotIdx);
  }
  let uniqueName = baseName;
  let counter = 1;
  while (fileNames.includes(uniqueName)) {
    uniqueName = `${name}_(${counter})${ext}`;
    counter++;
  }
  return uniqueName;
}

// Helper to get tag or name for filename
function getChecklistTagOrName(checklist: Checklist, objects: AventiObject[]): string {
  // Try checklist first
  let tag = checklist.tfmTag || checklist.rdsTag || checklist.name;
  if (tag && tag.length > 0) {
    return tag;
  }
  // Fallback: find object by checklist.objectid
  if (checklist.objectid && objects && objects.length > 0) {
    const obj = objects.find(o => o.id === checklist.objectid);
    if (obj) {
      tag = obj.tfmTag || obj.rdsTag || obj.name;
      if (tag && tag.length > 0) {
        return tag;
      }
    }
  }
  return 'fil'; // fallback if nothing found
}

// Helper to generate unique PDF filename and add to array
function addPdfWithFileName(
  pdf: AventiFile,
  checklist: Checklist,
  objects: AventiObject[],
  fileNames: string[],
  pdfArray: AventiFile[]
): void {
  pdf.checklistcomment = pdf.checklistcomment ?? checklist.comment ?? '';
  const tagOrName = getChecklistTagOrName(checklist, objects);
  let baseFileName = `KS_${checklist.type.name}_${tagOrName}.pdf`;
  baseFileName = baseFileName.replace(/[\/*?"<>|:]/g, '_');
  pdf.fileName = getUniqueFileName(baseFileName, fileNames);
  fileNames.push(pdf.fileName);
  pdfArray.push(pdf);
}

@Component({
  selector: 'app-checklist-list',
  templateUrl: './checklist-list.component.html',
  styleUrls: ['./checklist-list.component.css'],
  providers: [ObjectService],
})
export class ChecklistListComponent
  implements OnInit, OnDestroy, AfterViewInit {

  disableRoute = false;
  @Input() type = 'checklist';
  isEmpty = false;
  objects: AventiObject[];
  checklists: Checklist[];
  checklistsFrontend: Checklist[];
  user: User;
  checklistpdfs: AventiFile[];
  checklistpdfsnotsent: AventiFile[] = [];
  pdfsLoaded: boolean;
  pdfZipRequestPending = false;
  private pdfZipDestroyed = false;
  private pendingPdfStart: {action: string; projectId: string; requestId: string; files: {path: string; name: string}[]};

  mobileQuery: MediaQueryList;
  isLoading = true;

  @ViewChild(Table) dataTable: Table;
  selectedRows: Checklist[] = [];
  tableFiltersReady = false;
  pauseSorting = false;
  resetTable = false;
  numberOfTableSorts = 0;
  numberOfTableFilters = 0;
  tableSort: SortMeta[];
  tableFilters: { [p: string]: FilterMetadata } = {};
  sortButtons: SortButton[] = [];
  filterFields: string[] = [];
  displayedColumns: string[] = [];
  searchString = '';

  statuses: any[] = [];
  processes: any[] = [];
  descs: any[] = [];
  comments: any[] = [];
  types: any[] = [];
  profileNumbers: any[] = [];

  pageIndex = 0;
  pageSize = 25;
  numberOfSentToCustomer = 0;
  numberOfNotSentToCustomer = 0;
  numberOfNotGenerated: number;
  numberOfDeviations: number;
  params: Params;
  isOnline: boolean;
  projectId: string;
  @ViewChild('dataTable') dt: Table;
  private ngUnsubscribe = new Subject();

  constructor(
    private checklistservice: ChecklistService,
    media: MediaMatcher,
    private router: Router,
    private sharedservice: SharedService,
    private dateservice: DateService,
    private ptableService: TableService,
    private popupservice: PopupService,
    private idservice: IdService,
    private bottomSheet: MatBottomSheet,
    private snackbarservice: SnackbarService,
    private excelservice: ExcelService,
    private fileservice: FileService,
  ) {
    this.mobileQuery = media.matchMedia(environment.mobileQueryMaxwidthSmall);
  }

  async ngAfterViewInit() {
    await this.buildChecklists();
  }

  ngOnDestroy() {
    this.pdfZipDestroyed = true;
    this.ngUnsubscribe.next(null);
    this.ngUnsubscribe.complete();
  }

  async ngOnInit(): Promise<void> {

    if (this.type === 'receiptform') {
      this.sortButtons = [
        {name: 'Navn', sort: {field: 'nameSearchString', order: 0}},
        {name: 'Beskrivelse', sort: {field: 'desc', order: 0}},
        {name: 'Kommentar', sort: {field: 'comment', order: 0}},
        {name: 'Tilhørende objekt', sort: {field: 'receiptFormObjects', order: 0}},
        {name: 'Status', sort: {field: 'status', order: 0}},
        {name: 'Levert kunde', sort: {field: 'PDFDeliveredToCustomerBoolean', order: 0}},
        {name: 'Antall avvik', sort: {field: 'numberOfDeviations', order: 0}}
      ];
    } else {
      this.sortButtons = [
        {name: 'Prosess', sort: {field: 'process', order: 0}},
        {name: 'Type', sort: {field: 'typename', order: 0}},
        {name: 'Objektnavn', sort: {field: 'nameSearchString', order: 0}},
        {name: 'Beskrivelse', sort: {field: 'desc', order: 0}},
        {name: 'Kommentar', sort: {field: 'comment', order: 0}},
        {name: 'Profilnummer', sort: {field: 'profilenumber', order: 0}},
        {name: 'Status', sort: {field: 'status', order: 0}},
        {name: 'Levert kunde', sort: {field: 'PDFDeliveredToCustomerBoolean', order: 0}},
        {name: 'Antall avvik', sort: {field: 'numberOfDeviations', order: 0}}
      ];
    }

    this.projectId = this.idservice.projectId();

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationStart),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(async () => {
        if (!this.resetTable && !this.tableFiltersReady) {
          await this.callSetTableSortAndFilterFromQueryParams();
        }
      });

    this.sharedservice.online.subscribe(online => {
      this.isOnline = online;
    });

    this.sharedservice.user
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(async (user) => {
        if (user !== null) {
          if (user && !this.user) {
            this.user = user;
            await this.buildChecklists();
          }
        }
      });

    let datasourceObs: Observable<Checklist[]>;
    if (this.type === 'receiptform') {
      datasourceObs = this.sharedservice.receiptforms;
    } else {
      datasourceObs = this.sharedservice.checklists;
    }
    datasourceObs
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(async (checklists: Checklist[]) => {
        if (checklists !== null) {
          this.checklists = checklists;
          let proceed = true;
          for (const checklist of this.checklists) {
            if (checklist.signedBy?.user?.id && checklist.status !== 'signed' && checklist.isSigned) {
              proceed = false;
              console.warn(`Checklist ${checklist.id} signed by user, but status not signed. Setting status to signed`, checklist);
              checklist.status = 'signed';
              await this.checklistservice.updateChecklist(checklist.objectid, checklist);
            }
          }
          if (proceed) {
            await this.buildChecklists();
          }
        }
      });

    this.sharedservice.objects
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((objects) => {
        if (objects !== null) {
          this.objects = objects;
        }
      });
  }


  async buildChecklists() {
    if (this.user && this.checklists) {
      this.numberOfDeviations = 0;
      if (this.objects) {
        this.numberOfNotGenerated =
          this.objects.length - this.checklists.length;
      }

      const checklistarrayAll: Checklist[] = [];
      this.numberOfSentToCustomer = 0;
      this.numberOfNotSentToCustomer = 0;
      this.checklistpdfsnotsent = [];
      const fileNames: string[] = [];

      for (const checklist of this.checklists) {

        checklist.typename = checklist.type?.name;
        checklist.nameSearchString = checklist.name;
        checklist.tfmRdsSearchString = checklist.tfmTag + checklist.rdsTag;

        let objectsSearchString = '';
        checklist.objects?.map(object => {
          objectsSearchString += object.name;
        });
        checklist.objectsSearchString = objectsSearchString;

        let tagsSearchString = '';
        checklist.tags?.map(tag => {
          tagsSearchString += tag.key + tag.value;
        });
        checklist.tagsSearchString = tagsSearchString;

        const newDesc = {
          label: checklist.desc,
          value: checklist.desc
        };
        if (checklist.desc?.length > 0 &&
          !this.descs.some(item => item.label === checklist.desc || item.value === checklist.desc)) {
          this.descs.push(newDesc);
        }

        const newComment = {
          label: checklist.comment,
          value: checklist.comment
        };
        if (checklist.comment?.length > 0 &&
          !this.comments.some(item => item.label === checklist.comment || item.value === checklist.comment)) {
          this.comments.push(newComment);
        }

        const newStaus = {
          label: checklist.statusHuman,
          value: checklist.status,
          count: 0
        };
        if (!this.statuses.some(item => item.label === checklist.statusHuman || item.value === checklist.status)) {
          this.statuses.push(newStaus);
        }

        const newProcess = {
          label: checklist.process,
          value: checklist.process
        };
        if (checklist.process?.length > 0 &&
          !this.processes.some(item => item.label === checklist.process || item.value === checklist.process)) {
          this.processes.push(newProcess);
        }

        const newType = {
          label: checklist.type?.name,
          value: checklist.type?.name
        };
        if (checklist.type?.name?.length > 0 &&
          !this.types.some(item => item.label === checklist.type?.name || item.value === checklist.type?.name)) {
          this.types.push(newType);
        }

        const newProfileNumber = {
          label: checklist.profilenumber,
          value: checklist.profilenumber
        };
        if (checklist.profilenumber?.length > 0 &&
          !this.profileNumbers.some(item => item.label === checklist.profilenumber || item.value === checklist.profilenumber)) {
          this.profileNumbers.push(newProfileNumber);
        }

        if (checklist.PDFDeliveredToCustomer) {
          checklist.PDFDeliveredToCustomer.pending = false;
        }
        if (checklist.pdfexports) {
          const regex = new RegExp(`.[^. ]* `, 'g');
          checklist.pdfexports.forEach((pdf) => {
            if (pdf.sizestring) {
              pdf.sizestring = pdf.sizestring.replace(regex, '');
            }
          });
          this.pdfsLoaded = true;
        }
        checklist.pdfexportsBoolean = checklist.pdfexports?.length > 0;

        this.numberOfDeviations += checklist.numberOfDeviations;

        if (!checklist.receiptform) {
          const isCustomer = this.user.role.name === 'customer';
          const isSigned = checklist.status === 'signed';
          const pdfsExist = checklist.pdfexports?.length > 0;
          const pdfDelivered = checklist.PDFDeliveredToCustomer?.status;

          if (isSigned && isCustomer) {
            if (!pdfDelivered) {
              checklist.pdfexports?.forEach((pdf) => {
                addPdfWithFileName(pdf, checklist, this.objects, fileNames, this.checklistpdfsnotsent);
              });
            }
            if (getPdfDeliveredToCustomerBoolean(checklist)) {
              this.numberOfSentToCustomer++;
            } else if (pdfsExist) {
              this.numberOfNotSentToCustomer++;
            }
          } else if (!isCustomer) {
            if (!pdfDelivered) {
              checklist.pdfexports?.forEach((pdf) => {
                addPdfWithFileName(pdf, checklist, this.objects, fileNames, this.checklistpdfsnotsent);
                this.numberOfNotSentToCustomer++;
              });
            } else {
              this.numberOfSentToCustomer++;
            }
          }
          checklist.PDFDeliveredToCustomerBoolean = getPdfDeliveredToCustomerBoolean(checklist);
          checklist.numberOfDeviations = checklist.numberOfDeviations > 0 ? checklist.numberOfDeviations : null;
        }
        checklistarrayAll.push(checklist);
      }
      this.profileNumbers.sort((a, b) => a.label.localeCompare(b.label));
      this.processes.sort((a, b) => a.label.localeCompare(b.label));
      this.types.sort((a, b) => a.label.localeCompare(b.label));
      // Logical status progression rather than alphabetical.
      const statusOrder = ['not_started', 'started', 'finished', 'signed'];
      this.statuses.sort((a, b) => {
        const ai = statusOrder.indexOf(a.value);
        const bi = statusOrder.indexOf(b.value);
        return (ai === -1 ? statusOrder.length : ai) - (bi === -1 ? statusOrder.length : bi);
      });

      this.statuses.forEach(status => {
        status.count = this.checklists.filter(cl => cl.status === status.value).length;
      });

      this.setDisplayedColumns();
      this.checklistsFrontend = structuredClone(checklistarrayAll);

      this.isLoading = false;
      await this.callSetTableSortAndFilterFromQueryParams();
    }
  }

  setDisplayedColumns() {
    if (this.mobileQuery.matches) {
      if (this.user.role?.name === 'customer') {
        this.displayedColumns = ['mobileName', 'mobileStatus'];
      } else {
        if (this.type === 'receiptform') {
          this.displayedColumns = [
            'mobileNameReceiptform',
            'mobileStatus',
          ];
        } else {
          this.displayedColumns = [
            'mobileName',
            'checklistpdfs',
            'pdfdeliveredToCustomer',
          ];
        }
      }
    } else {
      if (this.user.role?.name === 'customer') {
        this.displayedColumns = [
          'name',
          'profilenumber',
          'type',
          'tags',
          'comment',
          'deviations',
          'statusHuman',
        ];
      } else {
        if (this.type === 'receiptform') {
          this.displayedColumns = [
            'name',
            'objects',
            'comment',
            'deviations',
            'statusHuman',
            'pdfdate',
            'lastupdated',
          ];
        } else {
          this.displayedColumns = [
            'process',
            'type',
            'name',
            'desc',
            'profilenumber',
            'tags',
            'comment',
            'deviations',
            'statusHuman',
            'checklistpdfs',
            'pdfdeliveredToCustomer',
            'pdfdate',
            'lastupdated',
          ];
        }
      }
    }
    this.filterFields = this.displayedColumns;
    this.filterFields.push('nameSearchString', 'objectsSearchString', 'typename', 'status', 'statusSearchString', 'PDFDeliveredToCustomerBoolean', 'numberOfDeviations', 'receiptFormObjects');
  }

  async setDeliveredToCustomer(checklist: Checklist) {
    let s = true;
    if (checklist.PDFDeliveredToCustomer) {
      checklist.PDFDeliveredToCustomer.pending = true;
      if (checklist.PDFDeliveredToCustomer.status) {
        s = false;
      }
    } else {
      checklist.PDFDeliveredToCustomer = {status: false, pending: true};
    }
    await this.checklistservice.updateChecklistDeliveredPdfToCustomer(
      s,
      checklist,
      this.user
    );
  }

  toggleDisableRoute() {
    this.disableRoute = true;
    setTimeout(() => this.disableRoute = false, 100);
  }

  async reopenPdfExports() {
    await this.router.navigate(['/p', this.projectId, 'pdf-eksporter']);
  }

  async downloadAllPdfsNotSent() {
    if (this.pdfZipRequestPending || !this.user?.superAdmin ||
        (!this.pendingPdfStart && !this.checklistpdfsnotsent.length)) { return; }
    this.pdfZipRequestPending = true;
    // Retain the exact selection and idempotency key if the callable response is lost.
    this.pendingPdfStart ||= {
      action: 'start', projectId: this.projectId, requestId: crypto.randomUUID(),
      files: this.checklistpdfsnotsent.map(file => ({path: file.path, name: file.fileName})),
    };
    try {
      const result = await this.fileservice.checklistPdfZip(this.pendingPdfStart);
      this.pendingPdfStart = undefined;
      if (!this.pdfZipDestroyed) {
        await this.router.navigate(['/p', this.projectId, 'pdf-eksporter'], {queryParams: {export: result.job.id}});
      }
    } catch (error) {
      if (['functions/invalid-argument', 'functions/already-exists', 'functions/not-found',
        'functions/failed-precondition', 'functions/permission-denied'].includes(error?.code)) {
        this.pendingPdfStart = undefined;
      }
      console.error('Server PDF ZIP export failed', error);
      if (!this.pdfZipDestroyed) { this.snackbarservice.openSnackBar(error?.message || String(error), ''); }
    }
    finally { this.pdfZipRequestPending = false; }
  }

  async onSort(event: any) {
    if (this.tableFiltersReady && !this.pauseSorting && !this.resetTable) {
      const sortArray: SortMeta[] = event.multisortmeta;
      this.tableSort = sortArray;
      this.sortButtons =
        await this.ptableService.onSort(sortArray, this.sortButtons, this.tableSort, this.tableFilters, this.tableFiltersReady);
    }
  }

  async sortFromButton(button: SortButton) {
    this.pauseSorting = true;
    const {
      tableSort,
      numberOfTableSorts,
      sortButtons
    } = await this.ptableService.sortFromButton(
      button,
      this.sortButtons,
      this.tableSort,
      this.tableFilters,
      this.tableFiltersReady,
      this.numberOfTableSorts
    );
    this.tableSort = tableSort;
    this.numberOfTableSorts = numberOfTableSorts;
    this.sortButtons = sortButtons;
    this.pauseSorting = false;
  }

  async clearTable(table: Table) {
    this.resetTable = true;
    this.searchString = '';
    const {
      tableSort,
      numberOfTableFilters,
      numberOfTableSorts,
      sortButtons
    } = await this.ptableService.clearTable(table, this.tableSort, this.sortButtons);
    this.tableSort = tableSort;
    this.numberOfTableFilters = numberOfTableFilters;
    this.numberOfTableSorts = numberOfTableSorts;
    this.sortButtons = sortButtons;
    this.resetTable = false;
  }

  async resetTableSort() {
    const {
      tableSort,
      sortButtons,
      tableFilters,
      numberOfTableFilters,
      numberOfTableSorts,
      searchString
    } = await this.ptableService.resetTableSort(
      this.tableSort,
      this.sortButtons,
      this.tableFilters,
      this.tableFiltersReady,
      this.tableFilters
    );
    this.tableSort = tableSort;
    this.sortButtons = sortButtons;
    this.tableFilters = tableFilters;
    this.numberOfTableFilters = numberOfTableFilters;
    this.numberOfTableSorts = numberOfTableSorts;
    this.searchString = searchString;
  }

  async onFilter(event: any) {
    if (this.tableFiltersReady && !this.resetTable) {
      const eventFilters: { [p: string]: FilterMetadata } = event.filters;
      this.numberOfTableFilters = await this.ptableService.setQueryParamsFromTableEvents(
        this.tableSort,
        this.tableFiltersReady,
        eventFilters
      );
      this.statuses.forEach(status => {
        status.count = this.checklists.filter(cl => cl.status === status.value && event.filteredValue.some(v => v.id === cl.id)).length;
      });
    }
  }

  toggleStatusFilter(value: string) {
    const newTableFilter = {...this.tableFilters};
    let newValue = newTableFilter.status?.value ?? null;
    if (newValue?.includes(value)) {
      const index = newValue.indexOf(value);
      newValue.splice(index, 1);
      if (newValue.length === 0) {
        newValue = null;
      }
    } else {
      if (newValue === null) {
        newValue = [value];
      } else {
        newValue.push(value);
      }
    }
    if (!newTableFilter.status) {
      newTableFilter.status = {value: null, matchMode: 'in'};
    }
    newTableFilter.status.value = newValue;
    if (newValue !== null) {
      newTableFilter.status.operator = 'and';
    } else {
      delete newTableFilter.status.operator;
    }
    this.dt.filter(newValue, 'status', 'in');
    this.tableFilters = newTableFilter;
    this.dt.filters = newTableFilter;
  }

  getSeverity(status: string) {
    return this.ptableService.getChecklistStatusSeverity(status);
  }

  async navigateToChecklistWithList(checklist: Checklist) {
    // Use filtered data if available, otherwise use all data
    const dataSource = this.dt?.filteredValue || this.checklistsFrontend;

    // Build a list of objects with id, name, tfmTag, rdsTag, objectId, objectType
    const orderedList = dataSource.map((c: Checklist) => ({
      index: dataSource.indexOf(c),
      id: c.id,
      name: c.name,
      tfmTag: c.tfmTag,
      rdsTag: c.rdsTag,
      objectId: c.objectid,
      objectType: c.type?.name || '',
      status: c.statusHuman,
      severity: this.getSeverity(c.status),
    }));
    const currentIndex = orderedList.findIndex(c => c.id === checklist.id);
    await this.ptableService.openChecklist(checklist, this.disableRoute, this.type, orderedList, currentIndex);
  }

  async onRowSelect(event: any) {
    if (this.mobileQuery.matches) {
      await this.navigateToChecklistWithList(event.data);
    }
  }

  async onRowUnSelect(event: any) {
    if (!this.mobileQuery.matches) {
      await this.navigateToChecklistWithList(event.data);
    }
  }

  async onPageChange(event: any) {
    await this.ptableService.onPageChange(event.rows, event.first);
    this.pageSize = event.rows;
    // pageIndex feeds [first] (a row offset), and is restored from pageIndex_table
    // (= event.first). Store the offset here too, not a page number, so the table
    // does not snap to the wrong row after paging.
    this.pageIndex = event.first;
  }

  newReceiptform() {
    this.popupservice.openDialogNewReceiptForm(this.user, this.projectId);
  }

  async deleteMultipleChecklists() {

    const sheet = this.bottomSheet.open(ConfirmComponent, {
      backdropClass: 'blur-dialog-backdrop',
      data: {
        title: `Slette sjekklister?`,
        text: `${this.selectedRows.length} sjekklister vil bli slettet:`,
        list: this.selectedRows.map((checklist) => checklist.name),
        yes: {
          name: 'Slett',
          icon: 'delete_forever',
        },
        no: {
          name: 'Avbryt',
          icon: 'close',
        },
      },
    });

    sheet
      .afterDismissed()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(async (confirm) => {
        if (confirm) {
          this.sharedservice.isLoading.next([
            true,
            'Vennligst vent...',
            [
              `Sletter ${this.selectedRows.length} sjekklister...`
            ],
          ]);

          for (const checklist of this.selectedRows) {
            await this.checklistservice.deleteChecklist(checklist, false);
          }

          this.sharedservice.isLoading.next([false, '', [], []]);
          this.snackbarservice.openSnackBar(
            `${this.selectedRows.length} sjekklister ble slettet`,
            ''
          );
          this.selectedRows = [];
        }
      });

  }

  exportChecklistsToExcel() {
    const allChecklists: Checklist[] = this.checklistsFrontend || [];
    const filtered: Checklist[] = this.dt?.filteredValue;
    // Each option carries the rows it exports, so the dismiss handler needs no lookup.
    const options = [];
    if (this.selectedRows.length > 0) {
      options.push({
        name: 'Valgte sjekklister',
        icon: 'checklist',
        value: this.selectedRows,
        count: this.selectedRows.length,
      });
    }
    if (filtered) {
      options.push({
        name: 'Filtrerte sjekklister',
        icon: 'filter_alt',
        value: filtered,
        count: filtered.length,
      });
    }
    if (options.length === 0) {
      this.exportChecklistRowsToExcel(allChecklists);
      return;
    }
    options.push({
      name: 'Alle sjekklister i prosjektet',
      icon: 'select_all',
      value: allChecklists,
      count: allChecklists.length,
    });

    const label = this.type === 'receiptform' ? 'mottakskontroller' : 'sjekklister';
    const sheet = this.bottomSheet.open(ConfirmComponent, {
      backdropClass: 'blur-dialog-backdrop',
      data: {
        title: `Eksporter ${label} til Excel`,
        text: `Hvilke ${label} vil du eksportere?`,
        options,
        no: {
          name: 'Avbryt',
          icon: 'close',
        },
      },
    });

    sheet
      .afterDismissed()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((rows) => {
        if (Array.isArray(rows)) {
          this.exportChecklistRowsToExcel(rows);
        }
      });
  }

  private exportChecklistRowsToExcel(dataSource: Checklist[]) {
    const rows = dataSource.map((c) => {
      const pdfUrls = c.pdfexports?.map(p => p.downloadurl).filter(u => !!u).join('\n') || '';
      const pdfNames = c.pdfexports?.map(p => p.name).filter(n => !!n).join('\n') || '';
      const tagsString = c.tags?.map(t => `${t.key}=${t.value}`).join('; ') || '';
      const objectNames = c.objects?.map(o => o.name).filter(n => !!n).join(', ') || '';
      const signedAt = c.signedBy?.timestamp ? this.dateservice.dateAsYYYYMMDDHHNNSS(c.signedBy.timestamp) : '';
      const lastUpdated = c.lastupdated ? this.dateservice.dateAsYYYYMMDDHHNNSS(c.lastupdated) : '';
      return {
        checklistid: c.id,
        objectid: c.objectid,
        name: c.name,
        tfmTag: c.tfmTag || '',
        rdsTag: c.rdsTag || '',
        process: c.process || '',
        type: c.typename || c.type?.name || '',
        desc: c.desc || '',
        comment: c.comment || '',
        profilenumber: c.profilenumber || '',
        tags: tagsString,
        status: c.statusHuman || c.status || '',
        numberOfDeviations: c.numberOfDeviations || 0,
        pdfCount: c.pdfexports?.length || 0,
        pdfNames,
        pdfUrls,
        deliveredToCustomer: c.PDFDeliveredToCustomerBoolean ? 'Ja' : 'Nei',
        signedBy: c.signedBy?.user ? `${c.signedBy.user.firstname || ''} ${c.signedBy.user.lastname || ''}`.trim() : '',
        signedAt,
        lastupdated: lastUpdated,
        objects: objectNames,
      };
    });
    const filename = this.type === 'receiptform' ? 'mottakskontroller' : 'sjekklister';
    this.excelservice.exportAsExcelFile(rows, filename);
  }

  exportToTaskCtrl() {
    const dataSource: Checklist[] = this.dt?.filteredValue || this.checklistsFrontend || [];
    const rows: any[] = [];
    for (const c of dataSource) {
      if (!c.pdfexports?.length) {
        continue;
      }
      for (const {} of c.pdfexports) {
        const type = c.typename || c.type?.name || '';
        const tfm = c.tfmTag || '';
        rows.push({
          Type: type,
          TFM: tfm,
          Filnavn: `KS_${type}_${tfm}`,
          RDS: c.rdsTag || '',
          Navn: c.name || '',
          Prosess: c.process || '',
          Beskrivelse: c.desc || '',
          Kommentar: c.comment || '',
          Profilnummer: c.profilenumber || '',
          Status: c.statusHuman || c.status || '',
          'Levert kunde': c.PDFDeliveredToCustomerBoolean ? 'Ja' : 'Nei',
        });
      }
    }
    if (rows.length === 0) {
      this.snackbarservice.openSnackBar(
        'Ingen sjekklister med PDF i filtrert liste',
        ''
      );
      return;
    }
    this.excelservice.exportAsExcelFile(rows, 'taskctrl');
  }

  async markAllFilteredAsDeliveredToCustomer() {
    const dataSource: Checklist[] = this.dt?.filteredValue || this.checklistsFrontend || [];
    const candidates = dataSource.filter(
      c => c.status === 'signed'
        && c.pdfexports?.length > 0
        && !c.PDFDeliveredToCustomer?.status
    );
    if (candidates.length === 0) {
      this.snackbarservice.openSnackBar(
        'Ingen sjekklister i filtrert liste kan merkes som "Levert kunde"',
        ''
      );
      return;
    }
    const sheet = this.bottomSheet.open(ConfirmComponent, {
      backdropClass: 'blur-dialog-backdrop',
      data: {
        title: 'Merk som "Levert kunde"?',
        text: `${candidates.length} sjekklister vil bli markert som "Levert kunde":`,
        list: candidates.map(c => c.name),
        yes: {
          name: 'Merk som levert',
          icon: 'send',
        },
        no: {
          name: 'Avbryt',
          icon: 'close',
        },
      },
    });

    sheet
      .afterDismissed()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(async (confirm) => {
        if (confirm) {
          this.sharedservice.isLoading.next([
            true,
            'Vennligst vent...',
            [`Markerer ${candidates.length} sjekklister som "Levert kunde"...`],
          ]);
          let done = 0;
          for (const c of candidates) {
            await this.setDeliveredToCustomer(c);
            done++;
          }
          this.sharedservice.isLoading.next([false, '', [], []]);
          this.snackbarservice.openSnackBar(
            `${done} sjekklister merket som "Levert kunde"`,
            ''
          );
        }
      });
  }

  private async callSetTableSortAndFilterFromQueryParams() {
    const result = this.ptableService.setTableSortAndFilterFromQueryParams(
      this.tableSort,
      this.sortButtons,
      this.tableFilters,
      this.tableFiltersReady
    );
    if (!this.tableFiltersReady) {
      setTimeout(async () => {
        this.tableFiltersReady = true;
        await this.callSetTableSortAndFilterFromQueryParams();
        const tablePages = await this.ptableService.getPagesOnLoad();
        this.pageSize = tablePages.pageSize;
        // A stored page offset belongs to the unfiltered list. Applying it to a
        // filtered (smaller) result set lands on an out-of-range page => empty table.
        // When a filter is active on restore, start at the first page.
        this.pageIndex = this.numberOfTableFilters > 0 ? 0 : tablePages.pageIndex;
      }, 1000);
    } else if (result) {
      this.tableSort = result.tableSort;
      this.sortButtons = result.sortButtons;
      this.tableFilters = result.tableFilters;
      this.numberOfTableFilters = result.numberOfTableFilters;
      this.numberOfTableSorts = result.numberOfTableSorts;
      this.searchString = result.searchString;
    }
  }
}
